/// Leaderboard read + write helpers.
///
/// Schema: `leaderboards/{levelId}/{difficulty}/{uid}`.
/// One entry per (level, difficulty, user). Per-difficulty
/// subcollections mean `orderBy score desc` queries don't need
/// a composite index.
///
/// Reads: direct Firestore query (auth required, gated by rule).
/// Writes: Cloud Function `submitLeaderboardEntry` ONLY — clients
/// have no write permission on `leaderboards/**`. The function
/// reads the user's profile via the admin SDK, validates the
/// score against a plausibility cap (50k) plus structural checks,
/// and writes with a server timestamp. This closes the
/// "edit-your-own-profile-then-submit" loophole the original
/// rule-based cross-check left open.
///
/// Writes are best-effort — leaderboards are decorative; a failure
/// to publish doesn't roll back the underlying recordLevelEnd.
/// All console.warn'd errors stay silent in the chrome.

import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
} from 'firebase/firestore'
import { httpsCallable } from '../firebase/callable'
import { firebaseFunctions, firestore } from '../firebase/app'
import type { DifficultyName, UserProfile } from './userProfile'
import { dailyBoardLevelId, DAILY_BOARD_SEGMENT } from './towerHome'

const TOP_N = 50

export interface LeaderboardEntry {
  uid: string
  displayName: string
  score: number
  difficulty: DifficultyName
  completedAt: number
  levelId: string
}

/// Build the displayName we publish to the leaderboard. Real names
/// come from the user's auth provider (Google/Apple); guest UIDs
/// get a short stable handle so the leaderboard isn't full of
/// blank cells.
export function leaderboardDisplayName(args: {
  uid: string
  displayName?: string | null
  isAnonymous: boolean
}): string {
  if (args.displayName && args.displayName.trim().length > 0) {
    return args.displayName.trim()
  }
  if (args.isAnonymous) {
    return 'Guest ' + args.uid.slice(-4).toUpperCase()
  }
  return 'Player ' + args.uid.slice(-4).toUpperCase()
}

/// Publish (or update) the player's best score for a given level +
/// difficulty. Reads the score from their cached profile so the
/// rule's cross-doc check has the same value to compare against.
/// Does nothing if the profile has no completed entry for that
/// combo — the rule would reject the write anyway.
/// Server-validated leaderboard publish. Calls the
/// `submitLeaderboardEntry` Cloud Function which reads the user's
/// profile via the admin SDK, validates the score, and writes
/// with a server timestamp. The client passes only the
/// (levelId, difficulty, displayName) — the score itself comes
/// from the profile doc the function reads, so a client lying
/// about the score is structurally impossible.
const submitLeaderboardEntryFn = httpsCallable<
  { levelId: string; difficulty: DifficultyName; displayName: string },
  { ok: true; score: number; displayName: string }
>(firebaseFunctions, 'submitLeaderboardEntry')

export async function publishLeaderboardEntry(args: {
  uid: string
  displayName: string
  levelId: string
  difficulty: DifficultyName
  profile: UserProfile
}): Promise<void> {
  const completed = args.profile.levelStats?.[args.levelId]?.completed?.[args.difficulty]
  if (!completed) return
  try {
    await submitLeaderboardEntryFn({
      levelId: args.levelId,
      difficulty: args.difficulty,
      displayName: args.displayName,
    })
  } catch (e) {
    // Logged not thrown — leaderboard publish is decorative.
    // Common reasons: function rate-limit, App Check failure,
    // out-of-range score, transient Cloud Functions error.
    console.warn('publishLeaderboardEntry failed', e)
  }
}

/// Fetch the top N scores for a level + difficulty. Sorted highest
/// first; missing entries (e.g., no one has completed at that tier
/// yet) yield an empty array.
export async function fetchLeaderboard(args: {
  levelId: string
  difficulty: DifficultyName
}): Promise<LeaderboardEntry[]> {
  const scoresRef = collection(
    firestore,
    'leaderboards',
    args.levelId,
    args.difficulty,
  )
  const q = query(scoresRef, orderBy('score', 'desc'), limit(TOP_N))
  const snap = await getDocs(q)
  const out: LeaderboardEntry[] = []
  snap.forEach((d) => {
    const data = d.data() as Partial<LeaderboardEntry>
    if (typeof data.score !== 'number') return
    out.push({
      uid: data.uid ?? d.id,
      displayName: data.displayName ?? 'Unknown',
      score: data.score,
      difficulty: args.difficulty,
      completedAt: data.completedAt ?? 0,
      levelId: args.levelId,
    })
  })
  return out
}

// ─── Day-scoped Daily Challenge board ──────────────────────────────
// Lives under the existing `leaderboards/DAILY_{date}/all/{uid}` path, so
// the existing read rule covers it (no rule change). A single board per day
// (all difficulties together); each row carries its own difficulty for display.

const submitDailyLeaderboardEntryFn = httpsCallable<
  { date: string; displayName: string },
  { ok: true; score: number; displayName: string }
>(firebaseFunctions, 'submitDailyLeaderboardEntry')

/// Publish the player's best score for today's daily to the day-scoped board.
/// The score is read server-side from the user's `daily` profile field
/// (validated against `date`), so the client can't lie about it. Best-effort.
export async function publishDailyLeaderboardEntry(args: {
  displayName: string
  date: string
}): Promise<void> {
  try {
    await submitDailyLeaderboardEntryFn({ date: args.date, displayName: args.displayName })
  } catch (e) {
    console.warn('publishDailyLeaderboardEntry failed', e)
  }
}

/// Fetch today's day-scoped daily-challenge board (top N by raw score).
export async function fetchDailyLeaderboard(date: string): Promise<LeaderboardEntry[]> {
  const scoresRef = collection(
    firestore,
    'leaderboards',
    dailyBoardLevelId(date),
    DAILY_BOARD_SEGMENT,
  )
  const q = query(scoresRef, orderBy('score', 'desc'), limit(TOP_N))
  const snap = await getDocs(q)
  const out: LeaderboardEntry[] = []
  snap.forEach((d) => {
    const data = d.data() as Partial<LeaderboardEntry>
    if (typeof data.score !== 'number') return
    out.push({
      uid: data.uid ?? d.id,
      displayName: data.displayName ?? 'Unknown',
      score: data.score,
      difficulty: (data.difficulty as DifficultyName) ?? 'Novice',
      completedAt: data.completedAt ?? 0,
      levelId: data.levelId ?? dailyBoardLevelId(date),
    })
  })
  return out
}
